import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import * as v from 'valibot'
import { getLocalizedRecordData } from 'src/i18n/localizedContent'
import type { ContestExtra } from 'src/services/helpers/schema/contestExtra'
import { localeAtom } from 'src/stores/locale'

/**
 * V2 contest endpoints type `extra` as `Record<string, unknown> | null`
 * because it's a freeform JSON column. Callers that want typed fields
 * from a known contest extra shape go through this cast.
 */
export function getContestExtra(
  contest: { extra?: Record<string, unknown> | null } | null | undefined,
): ContestExtra | null {
  return (contest?.extra as ContestExtra | null) ?? null
}

const contestSchema = v.object({
  description: v.string(),
  rule: v.string(),
  prize: v.string(),
})

const emptyContestData = { description: '', rule: '', prize: '' }

interface ContestWithLocales {
  locales?: Record<string, unknown> | null
}

function getContestData(lang: string, contest?: ContestWithLocales) {
  if (!contest?.locales) return emptyContestData

  const localeData = getLocalizedRecordData(lang, contest.locales)

  const parsedData = v.safeParse(contestSchema, localeData)

  if (!parsedData.success) {
    console.warn('Invalid contest localized data', parsedData.issues)
    return emptyContestData
  }
  return parsedData.output
}

export function useMemoizedContestData(contestData?: ContestWithLocales) {
  const locale = useAtomValue(localeAtom)
  return useMemo(
    () => getContestData(locale, contestData),
    [locale, contestData],
  )
}
